import { onFirstUserPresence, prefersReducedMotion, domBatcher, checkIsAboveFold } from "./utilities-BFAI3Dmv.js";
import { registerSentryFlush, flushVisiblePending, queueAnim, scheduleRevert } from "./anim-batch-nQ-NAxq8.js";
import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
import { w as waapi } from "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
let revealObserver = null;
const pendingObservations = [];
function _ensureObserver() {
  if (Shopify.designMode || prefersReducedMotion() || isMobileAnim || revealObserver)
    return;
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) {
          if (el.dataset.animRepeat !== "true") {
            revealObserver.unobserve(el);
            const i = pendingObservations.indexOf(el);
            if (i !== -1) pendingObservations.splice(i, 1);
            el._isObserved = false;
          }
          if (el._triggerReveal) el._triggerReveal();
        } else if (el.dataset.animRepeat === "true") {
          if (el.reset && el._initStandalone) {
            el.reset();
            el._scheduleStandaloneInit();
          }
        }
      });
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0 }
  );
  const _flushVisiblePending = () => flushVisiblePending(pendingObservations, revealObserver);
  registerSentryFlush(_flushVisiblePending);
  _flushVisiblePending();
  pendingObservations.forEach((el) => {
    revealObserver.observe(el);
    el._isObserved = true;
  });
}
onFirstUserPresence(_ensureObserver);
const CONTROLLER_SELECTOR = [
  "accordion-component",
  "slideshow-component",
  "media-bg-component",
  "tab-group",
  "custom-dialog",
  "cart-drawer",
  "quick-view-dialog",
  "quick-add-dialog",
  "sub-drawer",
  "custom-div-popup",
  ".megamenu-v2",
  "instagram-list-component",
  "listing-component",
  "collection-list-component",
  "product-listing-component",
  "icon-box-listing-component",
  "article-listing-component",
  "testimonial-listing-component",
  "recommendations-listing-component",
  "shop-the-look-product-listing",
  "dropdown-localization-component",
  "sorting-component",
  "paginate-component",
  "cr-dropdown",
  "marquee-v2"
].join(", ");
class AnimElement extends HTMLElement {
  connectedCallback() {
    var _a, _b;
    this._onRevealFocusIn || (this._onRevealFocusIn = () => this._releaseRevealOverflowClip());
    this.addEventListener("focusin", this._onRevealFocusIn);
    if (Shopify.designMode || prefersReducedMotion() || isMobileAnim) {
      this._releaseRevealOverflowClip();
      return;
    }
    let controller = this.closest(CONTROLLER_SELECTOR);
    if (controller) {
      const tag = controller.tagName.toLowerCase();
      if (tag === "dropdown-localization-component" && !this.closest(".i18n-drop"))
        controller = null;
      if ((tag === "sorting-component" || tag === "paginate-component") && !this.closest(".collection-sort-drop"))
        controller = null;
      if (tag === "cr-dropdown" && !this.closest(".cr-drop"))
        controller = null;
      if (controller && tag === "accordion-component" && window.matchMedia("(min-width: 992px)").matches && controller.dataset.collapseOnDesktop === "false") {
        const higherController = (_a = controller.parentElement) == null ? void 0 : _a.closest(CONTROLLER_SELECTOR);
        if (higherController) controller = higherController;
      }
      if (tag === "tab-group" && !this.closest("[data-tab-item]")) {
        const hasHigherController = controller.closest(
          "slideshow-component, media-bg-component, custom-dialog, cart-drawer, quick-view-dialog, quick-add-dialog, sub-drawer, custom-div-popup, .megamenu-v2"
        );
        if (!hasHigherController) {
          controller = null;
        } else {
          controller = hasHigherController;
        }
      }
    }
    if (controller) {
      const tag = controller.tagName.toLowerCase();
      if ([
        "slideshow-component",
        "instagram-list-component",
        "listing-component",
        "collection-list-component",
        "product-listing-component",
        "icon-box-listing-component",
        "article-listing-component",
        "testimonial-listing-component",
        "recommendations-listing-component",
        "shop-the-look-product-listing",
        "marquee-v2"
      ].includes(tag)) {
        if (tag !== "slideshow-component" && controller._isPlaying) {
          this.setup();
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
          return;
        }
        return;
      }
      if (tag === "tab-group") {
        const tabItem = this.closest("[data-tab-item]");
        if (tabItem) {
          if (tabItem.classList.contains("active")) {
            this.setup();
            domBatcher.write(() => {
              var _a2;
              return (_a2 = this.play) == null ? void 0 : _a2.call(this);
            });
          } else {
            domBatcher.write(() => {
              var _a2;
              return (_a2 = this.reset) == null ? void 0 : _a2.call(this);
            });
          }
        }
      } else if (tag !== "slideshow-component" && controller.classList.contains("active")) {
        if (controller.classList.contains("megamenu-v2")) {
          this.setup();
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        } else {
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.reset) == null ? void 0 : _a2.call(this);
          });
        }
      } else if ([
        "custom-dialog",
        "cart-drawer",
        "quick-view-dialog",
        "quick-add-dialog",
        "sub-drawer",
        "custom-div-popup",
        "accordion-component"
      ].includes(tag)) {
        if (tag === "accordion-component") {
          if (controller.isOpen) {
            this.setup();
            domBatcher.write(() => {
              var _a2;
              return (_a2 = this.play) == null ? void 0 : _a2.call(this);
            });
          }
        } else {
          const dialog = [
            "cart-drawer",
            "quick-add-dialog",
            "sub-drawer",
            "custom-div-popup"
          ].includes(tag) ? controller : controller.querySelector("dialog");
          if (dialog && (dialog.open || dialog.hasAttribute("open")) && (tag !== "quick-add-dialog" || !controller.hasAttribute("data-loading"))) {
            this.setup();
            domBatcher.write(() => {
              var _a2;
              return (_a2 = this.play) == null ? void 0 : _a2.call(this);
            });
          }
        }
      } else if (tag === "dropdown-localization-component") {
        if (controller.classList.contains("i18n-active")) {
          this.setup();
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
      } else if (tag === "sorting-component" || tag === "paginate-component") {
        if ((_b = controller.querySelector(".collection-sort-drop")) == null ? void 0 : _b.classList.contains("active")) {
          this.setup();
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
      } else if (tag === "cr-dropdown") {
        if (controller.classList.contains("cr-active")) {
          this.setup();
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
      }
      return;
    }
    if (this.dataset.animControlledOnly === "true") {
      this._releaseRevealOverflowClip();
      return;
    }
    if (document.body.dataset.animationOnScroll !== "true") {
      this._releaseRevealOverflowClip();
      return;
    }
    this._foldIO = new IntersectionObserver(
      (entries) => {
        this._foldIO.disconnect();
        this._foldIO = null;
        if (checkIsAboveFold(entries[0])) {
          this._releaseRevealOverflowClip();
          return;
        }
        this._scheduleStandaloneInit();
      },
      { threshold: 0 }
    );
    const foldRootSelector = this.dataset.animFoldRoot;
    const sharedFoldRoot = this.closest("[data-anim-fold-children]");
    const foldTarget = foldRootSelector ? this.closest(foldRootSelector) : sharedFoldRoot || this;
    this._foldIO.observe(foldTarget || this);
  }
  disconnectedCallback() {
    var _a;
    this._standaloneInitToken = (this._standaloneInitToken || 0) + 1;
    this.removeEventListener("focusin", this._onRevealFocusIn);
    this.removeEventListener("focusin", this._onStandaloneRevealFocusIn);
    if (this._foldIO) {
      this._foldIO.disconnect();
      this._foldIO = null;
    }
    revealObserver == null ? void 0 : revealObserver.unobserve(this);
    const pendingIndex = pendingObservations.indexOf(this);
    if (pendingIndex !== -1) pendingObservations.splice(pendingIndex, 1);
    this._isObserved = false;
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    this._anim = null;
    this._triggerReveal = null;
    this._releaseRevealOverflowClip();
    this._releaseRevealClipIfSettled();
  }
  // ── Helper ───────────────────────────────
  _getInner() {
    if (this.dataset.animTarget === "self") return this;
    return Array.from(this.children).find(
      (el) => el.tagName !== "STYLE" && el.tagName !== "SCRIPT"
    );
  }
  _clearAnimationStyles(target) {
    if (!target) return;
    target.style.removeProperty("opacity");
    target.style.removeProperty("transform");
    target.style.removeProperty("will-change");
    if (!target.getAttribute("style")) target.removeAttribute("style");
  }
  _readRevealOverflowRestore() {
    const value = this.style.getPropertyValue("overflow");
    let cssClip = false;
    if (!value) {
      const computed = window.getComputedStyle(this);
      cssClip = computed.overflowX === "clip" || computed.overflowY === "clip";
    }
    return {
      value: value === "clip" ? "" : value,
      priority: this.style.getPropertyPriority("overflow"),
      showAfterReveal: value === "clip" || cssClip
    };
  }
  _activateRevealOverflowClip(preparedRestore) {
    this._revealOverflowReleaseToken = (this._revealOverflowReleaseToken || 0) + 1;
    if (this.matches(":focus-within")) {
      this._releaseRevealOverflowClip();
      return;
    }
    if (!this._revealOverflowRestore) {
      this._revealOverflowRestore = preparedRestore || this._readRevealOverflowRestore();
    }
    if (this.style.getPropertyValue("overflow") !== "clip") {
      this.style.setProperty("overflow", "clip");
    }
  }
  _releaseRevealOverflowClip() {
    const restore = this._revealOverflowRestore;
    if (restore) {
      this._revealOverflowReleaseToken = (this._revealOverflowReleaseToken || 0) + 1;
      const { value, priority, showAfterReveal } = restore;
      if (value) {
        this.style.setProperty("overflow", value, priority);
      } else if (showAfterReveal) {
        this.style.setProperty("overflow", "visible");
      } else {
        this.style.removeProperty("overflow");
      }
      this._revealOverflowRestore = null;
    } else {
      const value = this.style.getPropertyValue("overflow");
      if (value === "clip") {
        this._revealOverflowReleaseToken = (this._revealOverflowReleaseToken || 0) + 1;
        this.style.setProperty("overflow", "visible");
      } else if (!value && this.isConnected && !isMobileAnim) {
        const token = (this._revealOverflowReleaseToken || 0) + 1;
        this._revealOverflowReleaseToken = token;
        domBatcher.read(() => {
          if (this._revealOverflowReleaseToken !== token || this._revealOverflowRestore || !this.isConnected || this.style.getPropertyValue("overflow"))
            return;
          const computed = window.getComputedStyle(this);
          const hasRevealClip = computed.overflowX === "clip" || computed.overflowY === "clip";
          if (!hasRevealClip) return;
          domBatcher.write(() => {
            if (this._revealOverflowReleaseToken === token && !this._revealOverflowRestore && this.isConnected && !this.style.getPropertyValue("overflow")) {
              this.style.setProperty("overflow", "visible");
            }
          });
        });
      }
    }
    if (!this.getAttribute("style")) this.removeAttribute("style");
  }
  _getRevealClipRoot() {
    return this.closest("[data-anim-clip-during-reveal]") || this._revealClipRoot || null;
  }
  _activateRevealClip() {
    const clipRoot = this.closest("[data-anim-clip-during-reveal]");
    if (!clipRoot) return;
    this._revealClipRoot = clipRoot;
    this._revealClipPending = true;
    clipRoot.setAttribute("data-anim-clip-active", "");
  }
  _releaseRevealClipIfSettled() {
    const clipRoot = this._getRevealClipRoot();
    if (!clipRoot) return;
    this._revealClipPending = false;
    const hasPendingReveal = Array.from(
      clipRoot.querySelectorAll("anim-element")
    ).some((anim) => anim._revealClipPending);
    if (!hasPendingReveal) clipRoot.removeAttribute("data-anim-clip-active");
    if (!this.isConnected) this._revealClipRoot = null;
  }
  // ── Standalone (fire-once) ───────────────────────────────
  _scheduleStandaloneInit() {
    const token = (this._standaloneInitToken || 0) + 1;
    this._standaloneInitToken = token;
    const isCurrent = () => this.isConnected && this._standaloneInitToken === token;
    domBatcher.read(() => {
      if (!isCurrent()) return;
      const restore = this._revealOverflowRestore || this._readRevealOverflowRestore();
      domBatcher.write(() => {
        if (!isCurrent()) return;
        if (Shopify.designMode || prefersReducedMotion() || isMobileAnim) {
          this._releaseRevealOverflowClip();
          return;
        }
        this._initStandalone(restore);
      });
    });
  }
  _initStandalone(preparedRestore) {
    const inner = this._getInner();
    if (!inner) return;
    this._activateRevealOverflowClip(preparedRestore);
    this._activateRevealClip();
    const { duration, easing } = this._cfg();
    const baseDelay = parseInt(this.dataset.animDelay) || 0;
    const staggerAmount = parseInt(this.dataset.animStagger);
    const actualStagger = isNaN(staggerAmount) ? 100 : staggerAmount;
    this._hideLinks();
    const distance = this.dataset.animDistance || "100%";
    const scale = this.dataset.animScale;
    let willChange = "opacity";
    let transformStr = "";
    if (distance !== "0" && distance !== "none") {
      transformStr += `translateY(${distance}) `;
      willChange += ", transform";
    }
    if (scale) {
      transformStr += `scale(${scale}) `;
      if (!willChange.includes("transform")) willChange += ", transform";
    }
    inner.style.willChange = willChange;
    if (transformStr) {
      inner.style.transform = transformStr.trim();
    }
    inner.style.opacity = "0";
    this._triggerReveal = () => {
      this.removeEventListener("focusin", this._onStandaloneRevealFocusIn);
      if (prefersReducedMotion()) {
        this.reset({ force: true });
        return;
      }
      if (this.dataset.animRepeat !== "true") {
        this._isObserved = false;
      }
      this._queueAnim(inner, duration, easing, baseDelay, actualStagger, true);
    };
    this._onStandaloneRevealFocusIn || (this._onStandaloneRevealFocusIn = () => {
      if (this._isObserved && this._triggerReveal) {
        if (this.dataset.animRepeat !== "true") {
          revealObserver == null ? void 0 : revealObserver.unobserve(this);
          const i = pendingObservations.indexOf(this);
          if (i !== -1) pendingObservations.splice(i, 1);
        }
        this._triggerReveal();
      }
    });
    this.removeEventListener("focusin", this._onStandaloneRevealFocusIn);
    this.addEventListener("focusin", this._onStandaloneRevealFocusIn, {
      once: true
    });
    if (this.contains(document.activeElement)) {
      this.removeEventListener("focusin", this._onStandaloneRevealFocusIn);
      this._triggerReveal();
      return;
    }
    if (!pendingObservations.includes(this)) {
      pendingObservations.push(this);
    }
    if (revealObserver && !this._isObserved) {
      revealObserver.observe(this);
      this._isObserved = true;
    }
  }
  // ── Controlled (parent-managed) ──────────────────────────
  setup() {
    this._standaloneInitToken = (this._standaloneInitToken || 0) + 1;
    if (Shopify.designMode || prefersReducedMotion() || isMobileAnim) {
      this._releaseRevealOverflowClip();
      return;
    }
    if (this._ready) {
      this._activateRevealOverflowClip();
      this._activateRevealClip();
      if (this._persistAnim && this._inner) {
        const distance2 = this.dataset.animDistance || "100%";
        const scale2 = this.dataset.animScale;
        let transformStr2 = "";
        if (distance2 !== "0" && distance2 !== "none")
          transformStr2 += `translateY(${distance2}) `;
        if (scale2) transformStr2 += `scale(${scale2}) `;
        this._inner.style.willChange = "opacity, transform";
        if (transformStr2) this._inner.style.transform = transformStr2.trim();
        this._inner.style.opacity = "0";
      }
      return;
    }
    const inner = this._getInner();
    if (!inner) return;
    this._activateRevealOverflowClip();
    this._activateRevealClip();
    this._hideLinks();
    const distance = this.dataset.animDistance || "100%";
    const scale = this.dataset.animScale;
    let willChange = "opacity";
    let transformStr = "";
    if (distance !== "0" && distance !== "none") {
      transformStr += `translateY(${distance}) `;
      willChange += ", transform";
    }
    if (scale) {
      transformStr += `scale(${scale}) `;
      if (!willChange.includes("transform")) willChange += ", transform";
    }
    inner.style.willChange = willChange;
    if (transformStr) {
      inner.style.transform = transformStr.trim();
    }
    inner.style.opacity = "0";
    this._inner = inner;
    this._ready = true;
  }
  play(delayMs, options = {}) {
    var _a;
    if (Shopify.designMode || prefersReducedMotion() || isMobileAnim) {
      this._releaseRevealOverflowClip();
      return;
    }
    if (!this._ready) this.setup();
    if (!this._inner) return;
    this._activateRevealOverflowClip();
    this._activateRevealClip();
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    const { duration, easing } = this._cfg();
    const baseDelay = delayMs ?? (parseInt(this.dataset.animDelay) || 0);
    const staggerAmount = parseInt(this.dataset.animStagger);
    const actualStagger = isNaN(staggerAmount) ? 100 : staggerAmount;
    this._queueAnim(
      this._inner,
      duration,
      easing,
      baseDelay,
      actualStagger,
      false,
      options.preserveBaseDelay
    );
  }
  _queueAnim(inner, duration, easing, baseDelay, staggerAmount, isStandalone, preserveBaseDelay = false) {
    const el = this;
    queueAnim({
      el,
      animate(computedDelay, restoreClip) {
        el._restoreClip = restoreClip;
        let keyframes = {
          opacity: [
            0,
            1
          ]
        };
        const distance = el.dataset.animDistance || "100%";
        const scale = el.dataset.animScale;
        let startTransform = "";
        let endTransform = "";
        if (distance !== "0" && distance !== "none") {
          startTransform += `translateY(${distance}) `;
          endTransform += `translateY(0%) `;
        }
        if (scale) {
          startTransform += `scale(${scale}) `;
          endTransform += `scale(1) `;
        }
        if (startTransform || endTransform) {
          keyframes.transform = [
            startTransform.trim(),
            endTransform.trim()
          ];
        }
        inner.style.removeProperty("opacity");
        inner.style.removeProperty("transform");
        el._anim = waapi.animate(inner, {
          ...keyframes,
          duration,
          delay: computedDelay,
          ease: easing,
          composition: "replace",
          onComplete: () => {
            el._anim = null;
            el._releaseRevealOverflowClip();
            el._releaseRevealClipIfSettled();
            if (el._hasAnimLinks) {
              el._hasAnimLinks = false;
              el._playLinks();
            }
            if (el._restoreClip) {
              el._restoreClip();
              el._restoreClip = null;
            }
            scheduleRevert(() => {
              if (!el._anim) {
                if (isStandalone) {
                  el._clearAnimationStyles(inner);
                } else if (!el._persistAnim) {
                  el._clearAnimationStyles(inner);
                }
              }
            });
          }
        });
      },
      baseDelay,
      staggerAmount,
      isStandalone,
      preserveBaseDelay
    });
  }
  reset(options = {}) {
    var _a;
    this._standaloneInitToken = (this._standaloneInitToken || 0) + 1;
    const force = options.force === true;
    if (!force && (Shopify.designMode || prefersReducedMotion() || isMobileAnim)) {
      this._releaseRevealOverflowClip();
      return;
    }
    if (this.hasAttribute("data-anim-debug")) return;
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    this._anim = null;
    this._releaseRevealOverflowClip();
    this._releaseRevealClipIfSettled();
    if (this._restoreClip) {
      this._restoreClip();
      this._restoreClip = null;
    }
    const inner = this._inner || this._getInner();
    this._clearAnimationStyles(inner);
    this._inner = null;
    this._ready = false;
    const leftoverLinks = Array.from(
      this.querySelectorAll('[data-anim-link="true"]')
    );
    if (this.hasAttribute("data-anim-link")) leftoverLinks.unshift(this);
    leftoverLinks.forEach((el) => {
      el.removeAttribute("data-anim-link");
      el.style.removeProperty("transition");
      el.style.removeProperty("background-position-x");
      if (!el.getAttribute("style")) el.removeAttribute("style");
    });
    this._ready = false;
  }
  // ── Helpers ──────────────────────────────────────────────
  _hideLinks() {
    if (isMobileAnim) return;
    const links = this.tagName === "A" ? [this] : Array.from(this.querySelectorAll("a"));
    this._hasAnimLinks = false;
    links.forEach((link) => {
      const wrapper = link.closest(
        '.hover-link-underline, [data-link-hover-style="underline"]'
      );
      if (!wrapper && !link.classList.contains("hover-link-underline")) return;
      const bgEl = link.querySelector(".btn-custom-label, .inline-btn-label") || link;
      this._hasAnimLinks = true;
      bgEl.setAttribute("data-anim-link", "true");
      bgEl.style.setProperty("transition", "none", "important");
      bgEl.style.setProperty("background-position-x", "100%", "important");
    });
  }
  _playLinks() {
    if (isMobileAnim) return;
    const links = Array.from(this.querySelectorAll('[data-anim-link="true"]'));
    if (this.hasAttribute("data-anim-link")) links.unshift(this);
    if (!links.length) return;
    const { duration } = this._cfg();
    links.forEach((el) => {
      el.removeAttribute("data-anim-link");
      el.style.removeProperty("transition");
      el.style.removeProperty("background-position-x");
      if (!el.getAttribute("style")) el.removeAttribute("style");
      el.animate(
        [
          { backgroundPositionX: "100%" },
          { backgroundPositionX: "0%" }
        ],
        {
          duration,
          easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          fill: "none"
          // Drops seamlessly back onto the CSS state when finished
        }
      );
    });
  }
  // ── Config ───────────────────────────────────────────────
  _cfg() {
    return {
      duration: parseInt(this.dataset.animDuration) || 600,
      easing: this.dataset.animEasing || "cubic-bezier(0.16, 1, 0.3, 1)"
    };
  }
}
if (!customElements.get("anim-element")) {
  customElements.define("anim-element", AnimElement);
}
